import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { ADMIN_COMPANY_LOADING } from "../../constants/loading.constant";
import { useCompanyCore } from "../../cores/company";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { Company } from "../../cores/me/interfaces/api/get-meCompany";
import { useRouterCore } from "../../cores/router";
import AreYouSure from "../component/AreYouSure";
import CreateCompanyForm from "../component/companies/CreateCompanyForm";
import { InputWembii } from "../component/InputWembii";
import { NoData } from "../component/NoData";
import { PanelWembii } from "../component/PanelWembii";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useResellerCore } from "../../cores/reseller";
import _ from "lodash";
import { useGlobalCore } from "../../cores/globals";
import CompanyTable from "../component/companies/CompanyTable";
import CompanyCard from "../component/companies/CompanyCard";
import { COMPANY_CREATE_CONFIG, COMPANY_DELETE_FORM } from "../../constants/modal.constant";

export type CompanySortField =
  | "name"
  | "userCount"
  | "plan"
  | "reseller"
  | "owner"
  | "location"
  | "createdAt";
export type CompanySortDirection = "ASC" | "DESC";
type DatePeriod = "all" | "24h" | "7d" | "30d" | "90d" | "1y";

const dateFromPeriod = (period: DatePeriod) => {
  if (period === "all") return null;
  const hoursByPeriod: Record<Exclude<DatePeriod, "all">, number> = {
    "24h": 24,
    "7d": 24 * 7,
    "30d": 24 * 30,
    "90d": 24 * 90,
    "1y": 24 * 365,
  };
  return Date.now() - hoursByPeriod[period] * 60 * 60 * 1000;
};

export default function Companies() {
  const intl = useIntl();
  const { companies, companiesFilters, company, deleteCompany, getSearchCompanies, getCompanies } = useCompanyCore();
  const { goToEditCompany, goToSocial } = useRouterCore();
  const { user, isReseller, isAdmin } = useMeCore();
  const { turnOffModal, turnOnModal, getModalVisibility, isModal } = useGlobalCore();
  const { resellers, getResellers } = useResellerCore();
  const [debounceSearchText, setDebounceSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [search, setSearch] = useState("");
  const [reseller, setReseller] = useState(user?.reseller?.id);
  const [companiesView, setCompaniesView] = useState<"cards" | "table">("table");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [planId, setPlanId] = useState("");
  const [country, setCountry] = useState("");
  const [createdPeriod, setCreatedPeriod] = useState<DatePeriod>("all");
  const [sortBy, setSortBy] = useState<CompanySortField>("name");
  const [sortDirection, setSortDirection] = useState<CompanySortDirection>("ASC");

  useEffect(() => {
    if (!user?.userRole?.admin && !(user?.userRole?.reseller && user?.reseller))
      goToSocial();
  }, [user, goToSocial]);

  const debouncedSearch = useRef(
    _.debounce((search) => setSearch(search), 1000)
  ).current;

  const resellerId = useMemo(
    () => (user?.reseller?.id ? +user.reseller.id : undefined),
    [user]
  );

  useEffect(() => {
    debouncedSearch(debounceSearchText);
  }, [debounceSearchText, debouncedSearch]);

  useEffect(() => {
    getResellers();
  }, [getResellers]);

  useEffect(() => {
    getSearchCompanies({
      search,
      resellerId: reseller,
    });
  }, [company, search, getSearchCompanies, reseller]);

  useEffect(() => {
    if (company && company.reseller) setReseller(`${company.reseller.id}`);
  }, [resellers, company]);

  useEffect(() => {
    getCompanies({ resellerId });
  }, [getCompanies, resellerId, user]);

  useEffect(() => {
    setSelectedCompanyIds((prev) =>
      prev.filter((id) => companiesFilters.some(({ company }) => company.id === id))
    );
  }, [companiesFilters]);

  useEffect(() => {
    if (companiesView !== "table") setSelectedCompanyIds([]);
  }, [companiesView]);

  const planOptions = useMemo(
    () =>
      Array.from(
        new Map(
          companiesFilters
            .filter(({ plan }) => plan)
            .map(({ plan }) => [String(plan!.id), plan!])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [companiesFilters]
  );

  const countryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          companiesFilters
            .map(({ company: listedCompany }) => listedCompany.country?.trim())
            .filter(Boolean) as string[]
        )
      ).sort((a, b) => a.localeCompare(b)),
    [companiesFilters]
  );

  const displayedCompanies = useMemo(() => {
    const createdSince = dateFromPeriod(createdPeriod);
    const filtered = companiesFilters.filter(({ company: listedCompany, plan }) => {
      const matchesPlan = !planId || String(plan?.id || "") === planId;
      const matchesCountry = !country || listedCompany.country === country;
      const createdAt = listedCompany.createdAt
        ? new Date(listedCompany.createdAt).getTime()
        : 0;
      const matchesCreatedAt = createdSince === null || createdAt >= createdSince;
      return matchesPlan && matchesCountry && matchesCreatedAt;
    });

    const valueFor = (item: typeof filtered[number]): string | number => {
      const { company: listedCompany, owner, plan, userCount } = item;
      switch (sortBy) {
        case "userCount":
          return userCount || 0;
        case "plan":
          return plan?.name || "";
        case "reseller":
          return listedCompany.reseller?.name || "";
        case "owner":
          return owner ? `${owner.firstName} ${owner.lastName}` : "";
        case "location":
          return `${listedCompany.country || ""} ${listedCompany.city || ""}`;
        case "createdAt":
          return listedCompany.createdAt
            ? new Date(listedCompany.createdAt).getTime()
            : 0;
        default:
          return listedCompany.name || "";
      }
    };

    return [...filtered].sort((a, b) => {
      const left = valueFor(a);
      const right = valueFor(b);
      const comparison =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right), undefined, {
              sensitivity: "base",
            });
      return sortDirection === "ASC" ? comparison : -comparison;
    });
  }, [companiesFilters, country, createdPeriod, planId, sortBy, sortDirection]);

  const changeSort = useCallback((field: CompanySortField) => {
    setSortBy((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "ASC" ? "DESC" : "ASC"
        );
        return currentField;
      }
      setSortDirection(field === "createdAt" || field === "userCount" ? "DESC" : "ASC");
      return field;
    });
  }, []);

  const toggleSideModal = useCallback(() => {
    turnOnModal(COMPANY_CREATE_CONFIG, true);
  }, [turnOnModal]);

  const onDeleting = useCallback((company) => {
    turnOnModal(COMPANY_DELETE_FORM, true);
    setSelectedCompany(company);
  }, [turnOnModal]);

  const onEditCompany = (companyId: string) => {
    goToEditCompany(companyId, "companies");
  };

  const onDeleteCompany = () => {
    deleteCompany(selectedCompany?.id!, () => {
      setSelectedCompany(null);
      turnOffModal(COMPANY_DELETE_FORM);
    });
  };

  const toggleCompanySelection = useCallback((companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  }, []);

  const toggleAllVisibleCompanies = useCallback(() => {
    const visibleIds = displayedCompanies
      .filter(({ company: listedCompany }) => user?.userRole?.admin && listedCompany.id !== company?.id)
      .map(({ company: listedCompany }) => listedCompany.id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedCompanyIds.includes(id));

    setSelectedCompanyIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  }, [company?.id, displayedCompanies, selectedCompanyIds, user?.userRole?.admin]);

  const onBulkDeleteCompanies = useCallback(async () => {
    if (!selectedCompanyIds.length) return;
    const confirmed = window.confirm(
      intl.formatMessage(
        {
          id: "app.companies.bulk.deleteConfirm",
          defaultMessage: '¿Eliminar {count} empresas seleccionadas?',
        },
        { count: selectedCompanyIds.length }
      )
    );
    if (!confirmed) return;

    for (const companyId of selectedCompanyIds) {
      // eslint-disable-next-line no-await-in-loop
      await deleteCompany(companyId);
    }
    setSelectedCompanyIds([]);
  }, [deleteCompany, intl, selectedCompanyIds]);

  const canResellerCreate = useMemo(
    () =>
      isReseller &&
      user?.reseller &&
      user?.reseller.companyCreationLimit > companies.length,
    [companies.length, isReseller, user]
  );

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({id: "app.sidebar.company"})}
        rightChildren={
          <>
            {(isAdmin || canResellerCreate) && (
              <Button className="whiteButton" onClick={toggleSideModal}>
                {intl.formatMessage({ id: "app.adminCompanies.createCompany" })}
              </Button>
            )}
            {!isAdmin && !canResellerCreate && (
              <div className="py-2 px-3 wembii-border">
                <FontAwesomeIcon
                  className="mx-2"
                  size="1x"
                  icon={["fas", "exclamation-triangle"]}
                />{" "}
                {intl.formatMessage({ id: "app.adminCompanies.error3" })}
              </div>
            )}
          </>
        }
      />
      
      {!companies.length ? null : (
        <PanelWembii
          className="companies-filters-panel w-100 px-4 py-3 my-3"
          shadow
        >
          <Row>
            <Col xs="12" md="6" xl="3">
              <InputWembii
                label={intl.formatMessage({ id: "app.adminCompanies.search" })}
                type="text"
                placeholder={intl.formatMessage({ id: "app.adminCompanies.searchCompany" })}
                rounded
                onChange={(value) => setDebounceSearchText(value)}
                value={debounceSearchText}
                style={{ borderRadius: "0px 10px 10px 0px" }}
                icon={
                  <FontAwesomeIcon
                    icon={["fas", "search"]}
                    size="1x"
                    color="grey"
                    className="pointer mb-2"
                  />
                }
              />
            </Col>
            <Col xs="12" sm="6" md="3" xl="2">
              <InputWembii
                label="Reseller"
                type="select"
                onChange={(value: string) => setReseller(value)}
                value={reseller}
                disabled={user?.userRole?.reseller}
              >
                {[
                  { id: "", name: `${intl.formatMessage({ id: "app.adminUsers.all"  })}`  },
                  ...resellers
                ].map(
                  ({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  )
                )}
              </InputWembii>
            </Col>
            <Col xs="12" sm="6" md="3" xl="2">
              <InputWembii
                label={intl.formatMessage({
                  id: "app.companies.filters.plan",
                  defaultMessage: "Plan",
                })}
                type="select"
                onChange={setPlanId}
                value={planId}
              >
                <option value="">
                  {intl.formatMessage({ id: "app.adminUsers.all", defaultMessage: "Todos" })}
                </option>
                {planOptions.map((planOption) => (
                  <option key={planOption.id} value={planOption.id}>
                    {planOption.name}
                  </option>
                ))}
              </InputWembii>
            </Col>
            <Col xs="12" sm="6" md="4" xl="2">
              <InputWembii
                label={intl.formatMessage({
                  id: "app.companies.filters.country",
                  defaultMessage: "País",
                })}
                type="select"
                onChange={setCountry}
                value={country}
              >
                <option value="">
                  {intl.formatMessage({ id: "app.adminUsers.all", defaultMessage: "Todos" })}
                </option>
                {countryOptions.map((countryOption) => (
                  <option key={countryOption} value={countryOption}>
                    {countryOption}
                  </option>
                ))}
              </InputWembii>
            </Col>
            <Col xs="12" sm="6" md="4" xl="2">
              <InputWembii
                label={intl.formatMessage({
                  id: "app.companies.filters.created",
                  defaultMessage: "Cuenta creada",
                })}
                type="select"
                onChange={(value: string) => setCreatedPeriod(value as DatePeriod)}
                value={createdPeriod}
              >
                <option value="all">
                  {intl.formatMessage({ id: "app.adminUsers.all" })}
                </option>
                <option value="24h">
                  {intl.formatMessage({ id: "app.users.period24Hours" })}
                </option>
                <option value="7d">
                  {intl.formatMessage({ id: "app.users.period7Days" })}
                </option>
                <option value="30d">
                  {intl.formatMessage({ id: "app.users.period30Days" })}
                </option>
                <option value="90d">
                  {intl.formatMessage({ id: "app.users.period90Days" })}
                </option>
                <option value="1y">
                  {intl.formatMessage({ id: "app.users.period365Days" })}
                </option>
              </InputWembii>
            </Col>
            <Col
              xs="12"
              md="4"
              xl="1"
              className="companies-view-toggle d-flex align-items-center justify-content-end"
            >
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small font-weight-bold mb-0">
                  {intl.formatMessage({ id: "app.common.view" })}
                </span>
                <div className="view-style-buttons">
                  <Button
                    className="lbutton"
                    outline={companiesView === "cards"}
                    color="transparent"
                    onClick={() => setCompaniesView("cards")}
                    aria-pressed={companiesView === "cards"}
                  >
                    <FontAwesomeIcon icon={["fas", "table-cells-large"]} />
                  </Button>
                  <Button
                    className="rbutton"
                    outline={companiesView === "table"}
                    color="transparent"
                    onClick={() => setCompaniesView("table")}
                    aria-pressed={companiesView === "table"}
                  >
                    <FontAwesomeIcon icon={["fas", "table-list"]} />
                  </Button>
                </div>
              </div>
            </Col>
            {companiesView === "table" && user?.userRole?.admin && selectedCompanyIds.length > 0 && (
              <Col xs="12">
                <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 pt-2 border-top">
                  <div className="font-weight-bold mb-2 mb-lg-0">
                    {intl.formatMessage(
                      {
                        id: "app.companies.bulk.selectedCount",
                        defaultMessage: "{count} empresas seleccionadas",
                      },
                      { count: selectedCompanyIds.length }
                    )}
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: "0.75rem" }}>
                    <Button color="danger" outline onClick={onBulkDeleteCompanies}>
                      {intl.formatMessage({
                        id: "app.companies.bulk.delete",
                        defaultMessage: "Eliminar seleccionadas",
                      })}
                    </Button>
                    <Button color="secondary" outline onClick={() => setSelectedCompanyIds([])}>
                      {intl.formatMessage({
                        id: "app.companies.bulk.clear",
                        defaultMessage: "Limpiar selección",
                      })}
                    </Button>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </PanelWembii>
      )}
      <Content className="justify-content-center">
        <UpdateLoadingCard
          loadingId={ADMIN_COMPANY_LOADING}
          text={intl.formatMessage({ id: "app.adminCompanies.updatingCompany" })}
        />
        {!companies.length && (
          <NoData
            image="generic3"
            text={intl.formatMessage({ id: "app.adminCompanies.nothaveCompany" })}
            actionLabel={intl.formatMessage({ id: "app.adminCompanies.createCompany" })}
            action={toggleSideModal}
            horizontal
          />
        )}
        <Container fluid className="companies-list-container">
          {displayedCompanies.length > 0 ? (
            companiesView === "table" ? (
              <CompanyTable
                companies={displayedCompanies}
                onEditCompany={onEditCompany}
                onDeleteCompany={onDeleting}
                selectedIds={selectedCompanyIds}
                onToggleCompany={toggleCompanySelection}
                onToggleAllCompanies={toggleAllVisibleCompanies}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={changeSort}
              />
            ) : (
              <Row className="company-grid d-flex flex-wrap justify-content-center">
                {displayedCompanies.map((c) => (
                  <CompanyCard
                    key={c.company.id}
                    company={c.company}
                    role={c.role}
                    plan={c.plan}
                    owner={c.owner}
                    userCount={c.userCount}
                    onEditCompany={onEditCompany}
                    onDeleteCompany={() => onDeleting(c.company)}
                  />
                ))}
              </Row>
            )
          ) : null}
        </Container>
      </Content>
      <SideModal
        active={isModal(COMPANY_CREATE_CONFIG)}
        visible={getModalVisibility(COMPANY_CREATE_CONFIG)}
        onClose={() => {
          turnOffModal(COMPANY_CREATE_CONFIG);
        }}
      >
        <CreateCompanyForm
          asReseller={isReseller}
          onSubmit={() => {
            turnOffModal(COMPANY_CREATE_CONFIG);
            getCompanies({ resellerId });
          }}
        />
      </SideModal>
      <SideModal
        active={isModal(COMPANY_DELETE_FORM)}
        visible={getModalVisibility(COMPANY_DELETE_FORM)}
        onClose={() => {
          setSelectedCompany(null);
          turnOffModal(COMPANY_DELETE_FORM);
        }}
      >
        {selectedCompany && (
          <AreYouSure
            message={`${intl.formatMessage({ id: "app.adminCompanies.error4" })} "${selectedCompany?.name}"`}
            onSubmit={onDeleteCompany}
            onClose={() => {
              setSelectedCompany(null);
              turnOffModal(COMPANY_DELETE_FORM);
            }}
          />
        )}
      </SideModal>
    </Layout>
  );
}
